import { makeStyles, Portal, Typography } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { Editor } from '@paintscout/ui-business'
import type { EditorMethods, EditorProps } from '@paintscout/ui-business'
import CloseIcon from '@material-ui/icons/Close'
import classnames from 'classnames'
import React, { useEffect, useState } from 'react'
import { useFrame } from 'react-frame-component'

/**
 * Sizes and colours are the prototype's note tray (ENG-2080). Its #354962 title / text colour has no
 * token (it sits between P40 and P35), so it is hardcoded, as PaymentsTableNext does.
 */
const useStyles = makeStyles(
  (theme: Theme) => {
    const neutral = theme.paintscout.palette.brand.Neutral
    return {
      backdrop: {
        position: 'fixed',
        inset: 0,
        zIndex: 1298,
        backgroundColor: 'rgba(255, 255, 255, 0.7)',
        opacity: 0,
        pointerEvents: 'none',
        transition: 'opacity 0.25s ease'
      },
      backdropOpen: {
        opacity: 1,
        pointerEvents: 'auto'
      },
      // Always mounted and parked below the screen, so the editor inside is already there to focus
      // during the tap that opens it — iOS only raises the keyboard for a focus made in the gesture.
      // Parked by transform only: `visibility: hidden` or `display: none` would make it unfocusable.
      sheet: {
        position: 'fixed',
        left: 0,
        right: 0,
        zIndex: 1299,
        transform: 'translateY(100%)',
        pointerEvents: 'none',
        transition: 'transform 0.32s cubic-bezier(0.16, 1, 0.3, 1), bottom 0.2s ease-out'
      },
      sheetOpen: {
        transform: 'translateY(0)',
        pointerEvents: 'auto'
      },
      panel: {
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        backgroundColor: neutral.White,
        border: `0.8px solid ${neutral.P85}`,
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)'
      },
      // While the keyboard is up the sheet sits on it, so its bottom edge has no border to draw.
      panelOnKeyboard: {
        borderBottom: 'none'
      },
      header: {
        height: 48,
        padding: theme.spacing(0, 2.5),
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1.5),
        flexShrink: 0,
        borderBottom: `0.8px solid ${neutral.P90}`
      },
      title: {
        fontSize: theme.typography.pxToRem(14),
        fontWeight: 500,
        color: '#354962',
        whiteSpace: 'nowrap'
      },
      headerActions: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1)
      },
      close: {
        width: 28,
        height: 28,
        marginRight: -6,
        padding: 0,
        border: 'none',
        borderRadius: 6,
        background: 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        color: neutral.P50,
        '&:active': { backgroundColor: '#f0f3f7' },
        '& svg': { fontSize: 16 }
      },
      body: {
        padding: theme.spacing(2.5),
        paddingBottom: 'calc(20px + env(safe-area-inset-bottom))',
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5)
      },
      bodyOnKeyboard: {
        paddingBottom: theme.spacing(2)
      },
      field: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1),
        border: `0.8px solid ${neutral.P85}`,
        borderRadius: 10,
        padding: theme.spacing(1.5),
        transition: 'border-color 0.15s, box-shadow 0.15s',
        '&:focus-within': {
          borderColor: '#2358ee',
          boxShadow: '0 0 0 3px rgba(35, 88, 238, 0.12)'
        }
      },
      fieldIcon: {
        width: 16,
        height: 16,
        fontSize: 16,
        flexShrink: 0,
        marginTop: 2,
        color: neutral.P50
      },
      // 16px text keeps iOS from zooming in on focus. `&&` outranks the Editor's own sizing.
      editor: {
        flex: 1,
        minWidth: 0,
        maxHeight: '40vh',
        overflowY: 'auto',
        '&& .ProseMirror, && .ProseMirror p, && .ProseMirror li': {
          fontSize: theme.typography.pxToRem(16),
          lineHeight: 1.4,
          color: '#354962'
        },
        // Five lines, like the prototype's five-row textarea.
        '&& .ProseMirror': {
          minHeight: '7em'
        },
        '&& .ProseMirror .is-editor-empty::before': {
          color: '#96a5b9'
        },
        // The Products editor's locked "Additional Work:" line, set as a label.
        '&& .ProseMirror p[data-section-label]': {
          fontSize: theme.typography.pxToRem(14),
          color: neutral.P60,
          marginTop: theme.spacing(2),
          marginBottom: theme.spacing(0.5),
          userSelect: 'none'
        }
      },
      // The Editor draws its own 1px outline (hidden only while focused) and a 120px floor. The field
      // above is the one outline here, so both go.
      editorRoot: {
        '&&': {
          border: 'none',
          boxShadow: 'none',
          minHeight: 0
        }
      }
    }
  },
  { name: 'WorkOrderNoteTray' }
)

export interface WorkOrderNoteTrayProps {
  open: boolean
  title: string
  icon: React.ComponentType<{ className?: string }>
  /** The note's saved HTML. The editor follows it while the tray is closed. */
  content: string
  placeholder?: string
  /** The editor's handle — the caller reads `getHTML()` from it to save, and focuses it to open. */
  editorRef: React.MutableRefObject<EditorMethods | null>
  /** Close request (backdrop, close button, Escape). The caller saves, then sets `open` false. */
  onClose: () => void
  /** Extra header controls, e.g. the product description's Reset. */
  headerAction?: React.ReactNode
  /** Extra TipTap extensions for the editor. Keep the array's identity stable across renders. */
  extensions?: EditorProps['extensions']
}

/**
 * The phone editor for a work-order note — Crew Note, or Products with its Additional Work (ENG-2080). Tapping the
 * note's row slides this tray up with the note in one outlined field, in place of the desktop's inline
 * editor with its toolbar. The note stays HTML, so formatting written on desktop survives an edit here.
 *
 * The caller opens it by focusing `editorRef` inside the tap, then setting `open` — the tray is always
 * mounted so that focus can land during the tap (see `sheet` in the styles). It rides above the
 * on-screen keyboard.
 */
export default function WorkOrderNoteTray({
  open,
  title,
  icon: Icon,
  content,
  placeholder,
  editorRef,
  onClose,
  headerAction,
  extensions
}: WorkOrderNoteTrayProps) {
  const classes = useStyles()
  // Inside a presentation preview iframe the tray must portal into the iframe's body, where its
  // JSS lives; the parent page has none of it. Outside a Frame these are the top document/window.
  const { document: frameDoc, window: frameWin } = useFrame()
  const keyboardOffset = useKeyboardOffset(open, frameWin)
  useHideUserflowLauncher(open)
  const onKeyboard = keyboardOffset > 0

  return (
    <Portal container={frameDoc?.body}>
      <div
        className={classnames(classes.backdrop, { [classes.backdropOpen]: open })}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        aria-hidden={!open}
        className={classnames(classes.sheet, { [classes.sheetOpen]: open })}
        style={{ bottom: keyboardOffset }}
        onKeyDown={(ev) => {
          if (ev.key === 'Escape') {
            ev.preventDefault()
            onClose()
          }
        }}
      >
        <div className={classnames(classes.panel, { [classes.panelOnKeyboard]: onKeyboard })}>
          <div className={classes.header}>
            <Typography component="span" className={classes.title}>
              {title}
            </Typography>
            <div className={classes.headerActions}>
              {headerAction}
              <button type="button" className={classes.close} aria-label="Close" onClick={onClose}>
                <CloseIcon />
              </button>
            </div>
          </div>
          <div className={classnames(classes.body, { [classes.bodyOnKeyboard]: onKeyboard })}>
            <div className={classes.field}>
              <Icon className={classes.fieldIcon} />
              <div className={classes.editor}>
                <Editor
                  ref={editorRef}
                  content={content || ''}
                  placeholder={placeholder}
                  extensions={extensions}
                  inline
                  disableToolbar
                  fullWidth
                  classes={{ root: classes.editorRoot }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  )
}

/**
 * How far the on-screen keyboard covers the bottom of the layout viewport, from `visualViewport`. A
 * `position: fixed; bottom: 0` element sits behind the iOS keyboard, so the tray lifts by this much.
 */
function useKeyboardOffset(active: boolean, win: Window | undefined) {
  const [offset, setOffset] = useState(0)
  useEffect(() => {
    const viewport = win?.visualViewport ?? null
    if (!active || !viewport) {
      setOffset(0)
      return
    }
    const update = () => setOffset(Math.max(0, Math.round(win.innerHeight - viewport.height - viewport.offsetTop)))
    update()
    viewport.addEventListener('resize', update)
    viewport.addEventListener('scroll', update)
    return () => {
      viewport.removeEventListener('resize', update)
      viewport.removeEventListener('scroll', update)
    }
  }, [active, win])
  return offset
}

const HIDE_USERFLOW_CLASS = 'ps-hide-userflow'
const HIDE_USERFLOW_STYLE_ID = 'ps-hide-userflow-styles'
let hideUserflowCount = 0

/**
 * Hides the Userflow launcher (z-index 1309, above the tray) while the tray is open. CSS only, so
 * it leaves `setResourceCenterLauncherHidden` to its other owners (painter seats, HubSpot chat).
 * Counted, because each note renders its own tray and one closing must not re-show it under another.
 */
function useHideUserflowLauncher(active: boolean) {
  useEffect(() => {
    if (!active || typeof document === 'undefined') {
      return
    }
    if (!document.getElementById(HIDE_USERFLOW_STYLE_ID)) {
      const style = document.createElement('style')
      style.id = HIDE_USERFLOW_STYLE_ID
      style.textContent = `body.${HIDE_USERFLOW_CLASS} #userflow-ui { display: none !important; }`
      document.head.appendChild(style)
    }
    hideUserflowCount += 1
    document.body.classList.add(HIDE_USERFLOW_CLASS)
    return () => {
      hideUserflowCount = Math.max(0, hideUserflowCount - 1)
      if (hideUserflowCount === 0) {
        document.body.classList.remove(HIDE_USERFLOW_CLASS)
      }
    }
  }, [active])
}
